// Coordination -> RFI Generator (owner, 2026-10-20: "start implementing the
// RFI-Generator for bidlight, as a separate Tab").
//
// The project's RFIs, newest first. New RFI starts a draft and opens it;
// clicking a row opens it in the editor (RfiEditor). Each RFI is built into
// one PDF - a cover in your company's format, then the exhibit pages - and
// every build is filed in Document Control.
//
// Who sees what is the server's answer: the list says whether this person may
// start one (can.create), and each RFI carries its own buttons (rfi.can).
// Partners see the tab on the owner's per-project switch.
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Button, Empty, Select, Space, Table, Tabs, Tag, Tooltip, Typography, message } from "antd";
import { FileDoneOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import { ExtraScopeChip } from "components/Coordination/ExtraScope";
import { createRfi, getRfi, listFeedRfis, listRfis } from "api/rfi/rfiApi";
import RfiFeedTab from "./RfiFeedTab";
import RfiEditor from "./RfiEditor";
import RfiTemplateDrawer from "./RfiTemplateDrawer";
import { STATUS } from "./rfiStatus";
import { matchOption } from "components/SearchSelect/searchSelect";

const { Text } = Typography;

export default function RfiPage() {
  const { projectId: routeProjectId } = useParams();
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  const [rememberedId] = useLastProject(projects);
  const projectId = routeProjectId || rememberedId || "";

  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // PENDING / ARCHIVE: RFIs read from Procore and ACC (RfiFeedTab). Project team only - the
  // server answers 403 to anyone else, and then the two tabs are not shown.
  const [tab, setTab] = useState("generator");
  const [feedCounts, setFeedCounts] = useState(null);
  const [feedAllowed, setFeedAllowed] = useState(false);
  useEffect(() => {
    let alive = true;
    setFeedAllowed(false); setFeedCounts(null); setTab("generator");
    if (!projectId) return undefined;
    listFeedRfis(projectId, "pending")
      .then((d) => { if (alive) { setFeedAllowed(true); setFeedCounts(d.counts); } })
      .catch(() => { if (alive) setFeedAllowed(false); });
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json())
      .then((l) => { if (alive) setProjects(Array.isArray(l) ? l : (l?.projects || [])); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(() => {
    if (!projectId) { setList(null); return; }
    setError("");
    listRfis(projectId)
      .then(setList)
      .catch((e) => { setList(null); setError(e.message || "Could not load the RFIs"); });
  }, [projectId]);
  useEffect(() => { load(); setOpen(null); }, [load]);

  const openRfi = async (id) => {
    try {
      const r = await getRfi(projectId, id);
      setOpen(r.rfi);
    } catch (e) { message.error(e.message || "Could not open the RFI"); }
  };
  const start = async () => {
    setCreating(true);
    try {
      const r = await createRfi(projectId, {});
      load();
      setOpen(r.rfi);
    } catch (e) {
      message.error(e.message || "Could not start an RFI");
    } finally { setCreating(false); }
  };

  const columns = [
    { title: "#", dataIndex: "seq", width: 60, render: (v, r) => (r.header && r.header.rfiNumber ? <Tooltip title={`Draft ${v}`}>{r.header.rfiNumber}</Tooltip> : <Text type="secondary">D{v}</Text>) },
    {
      title: "Subject",
      dataIndex: ["header", "subject"],
      ellipsis: true,
      // THE MARK TRAVELS WITH THE SUBJECT, not in a column of its own: most
      // RFIs carry no mark, and an empty column teaches the reader to stop
      // looking at it. Where this RFI is, is on the row too, because "is this
      // extra?" is only answerable next to "where".
      render: (v, r) => (
        <Space size={6} wrap>
          <span>{v || <Text type="secondary">(no subject yet)</Text>}</span>
          <ExtraScopeChip extraScope={r.extraScope} small />
        </Space>
      ),
    },
    {
      title: "Where",
      width: 170,
      ellipsis: true,
      render: (v, r) => {
        const sc = r.scope || {};
        const picked = [sc.level, sc.area, sc.system].map((x) => String(x || "").trim()).filter(Boolean).join(" \u00b7 ");
        if (picked) return picked;
        const typed = String((r.header && r.header.location) || "").trim();
        // A typed line has not been placed on the project: it prints on the
        // cover and nothing can be read from it, so it is said quietly.
        return typed ? <Tooltip title="Typed on the cover, not picked from the project - pick a level and area to place it"><Text type="secondary">{typed}</Text></Tooltip> : "";
      },
    },
    { title: "Status", dataIndex: "status", width: 110, render: (v) => <Tag color={(STATUS[v] || {}).color}>{(STATUS[v] || {}).label || v}</Tag> },
    { title: "To", dataIndex: ["header", "to"], width: 180, ellipsis: true },
    { title: "Due", dataIndex: ["header", "dueDate"], width: 110, render: (v) => (v ? moment(v, "YYYY-MM-DD").format("MMM D, YYYY") : "") },
    {
      title: "Package", width: 150,
      render: (_, r) => (!r.latestBuild
        ? <Text type="secondary">Not built</Text>
        : r.buildIsCurrent ? <Tag color="green">Up to date</Tag> : <Tag color="orange">Out of date</Tag>),
    },
    { title: "Written by", dataIndex: ["createdBy", "name"], width: 160, ellipsis: true },
    { title: "Updated", dataIndex: "updatedAt", width: 120, render: (v) => (v ? moment(v).fromNow() : "") },
  ];

  const generator = (
    <>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        <FileDoneOutlined /> Each RFI becomes one PDF: a cover in your company&apos;s format with the question, then the sheets,
        model views and location details as numbered exhibit pages. Every build is filed in Document Control.
      </Text>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {!projectId ? (
        <Empty description="Pick a project to see its RFIs" />
      ) : (
        <Table
          rowKey="_id" size="middle" columns={columns} loading={!list && !error}
          dataSource={(list && list.rfis) || []}
          onRow={(r) => ({ onClick: () => openRfi(r._id), style: { cursor: "pointer" } })}
          locale={{ emptyText: <Empty description={list && list.can && list.can.create ? "No RFIs yet - start one with New RFI" : "No RFIs on this project yet"} /> }}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          scroll={{ x: 900 }}
        />
      )}
    </>
  );

  return (
    <ErrorBoundary>
      <div style={{ padding: 24 }}>
        <CoordinationBreadcrumbs current="RFI Generator" />
        <Space style={{ marginBottom: 12 }} wrap>
          <Select filterOption={matchOption}
            style={{ minWidth: 280 }} showSearch optionFilterProp="label" placeholder="Select a project"
            value={projectId || undefined}
            onChange={(id) => history.push(`/rfi/${id}`)}
            options={(projects || []).map((p) => ({ value: String(p._id), label: p.title || p.name || "Untitled" }))}
          />
          {tab === "generator" && list && list.can && list.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={start} loading={creating}>New RFI</Button>
          )}
          {tab === "generator" && <Button icon={<SettingOutlined />} onClick={() => setTemplateOpen(true)}>Company template</Button>}
        </Space>
        {projectId && feedAllowed ? (
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              { key: "generator", label: "RFI Generator", children: generator },
              { key: "pending", label: `Pending${feedCounts ? ` (${feedCounts.pending})` : ""}`, children: tab === "pending" && <RfiFeedTab projectId={projectId} bucket="pending" onCounts={setFeedCounts} /> },
              { key: "archived", label: `Archive${feedCounts ? ` (${feedCounts.archived})` : ""}`, children: tab === "archived" && <RfiFeedTab projectId={projectId} bucket="archived" onCounts={setFeedCounts} /> },
            ]}
          />
        ) : generator}
        {open && (
          <RfiEditor key={open._id} projectId={projectId} rfi={open} onClose={() => setOpen(null)} onChanged={load} />
        )}
        {templateOpen && <RfiTemplateDrawer projectId={projectId} onClose={() => setTemplateOpen(false)} />}
      </div>
    </ErrorBoundary>
  );
}
